import createBootError from 'owa-shared-start/lib/createBootError';
import getScopedPath from 'owa-url/lib/getScopedPath';
import getSessionData from './utils/getSessionData';
import { getApp, getOwsPath } from 'owa-config';
import { lazySetAriaTenantToken } from 'mini-start-analytics';
import { markFunction, trackBottleneck } from 'owa-performance';
import { onBootComplete } from './utils/onBootComplete';
import { onBootError } from './utils/onBootError';
import { setApp } from 'owa-config/lib/bootstrapOptions';
import { setBootFailureCount } from 'owa-shared-start/lib/bootErrorCounter';
import { setIsDeepLink } from 'owa-url/lib/isDeepLink';
import { unblockLazyLoadCallbacks } from 'owa-bundling-light';
import { updateServiceConfig } from 'owa-service/lib/config';
import type StartConfig from './types/StartConfig';
import type { PromiseWithKey } from 'owa-performance';

let startTime: number;

/* eslint-disable-next-line @typescript-eslint/no-explicit-any --
 * See aka.ms/mini-lint-errors
 *	> Unexpected any. Specify a different type. */
export async function miniStart(config: StartConfig): Promise<any> {
    try {
        await internalStart(config);

        try {
            // Unblocking lazy-loading post successful boot
            unblockLazyLoadCallbacks();
            onBootComplete(config, startTime);

            // call loader removed callbacks
            config.onLoaderRemoved?.();

            setBootFailureCount(0);
        } catch (error) {
            throw createBootError(error, 'BootComplete');
        }
    } catch (bootError) {
        // Unblocking lazy-loading to enable lazy boot-failure logging
        unblockLazyLoadCallbacks();

        onBootError(bootError, config);
    }
}

async function internalStart(config: StartConfig) {
    try {
        startTime = Date.now();

        // don't reset the app if it is already set
        if (!getApp()) {
            setApp(config.app);
        }

        setIsDeepLink(config.isDeepLink);

        lazySetAriaTenantToken.importAndExecute(config.startupAriaToken);

        const runBeforeStartPromise: Promise<void> | undefined = config
            .runBeforeStart?.(config)
            .catch(error => {
                if (!error.source) {
                    error.source = 'BeforeBoot';
                }
                throw error;
            });

        updateServiceConfig({
            baseUrl: getScopedPath(getOwsPath()),
            appName: config.app,
        });

        const sessionDataFunction = config.overrideBootPromises || getSessionData;
        const sessionDataPromise = runBeforeStartPromise
            ? runBeforeStartPromise.then(sessionDataFunction)
            : sessionDataFunction();

        const javascriptPromise = markFunction(() => config.bootstrap.import(), 'mjs')();

        /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
         * See aka.ms/mini-lint-errors
         *	> Unexpected any. Specify a different type. */
        const bootPromises: PromiseWithKey<any>[] = [
            { promise: sessionDataPromise, key: 'sd' },
            { promise: javascriptPromise, key: 'js' },
        ];

        // TODO: Do we need getOpxHostData? Since it's already included in bootstrap flow

        /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
         * See aka.ms/mini-lint-errors
         *	> Unexpected any. Specify a different type. */
        const bootstrapPromises: Promise<any>[] = [javascriptPromise];

        bootPromises.push({
            promise: Promise.all(bootstrapPromises)
                .then(([bootFunc]) => {
                    try {
                        return bootFunc.apply(null, [sessionDataPromise]);
                    } catch (e) {
                        throw createBootError(e, 'Bootstrap');
                    }
                })
                .catch(e => {
                    throw createBootError(e, 'Script');
                }),
            key: null,
        });

        if (config.runAfterRequests) {
            config.runAfterRequests(sessionDataPromise);
        }

        return trackBottleneck('start', bootPromises);
    } catch (error) {
        return Promise.reject(createBootError(error, 'Preboot'));
    }
}
